import { Component, OnDestroy, OnInit, Inject, PLATFORM_ID, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { User } from '../../_models/user';
import { AlertService } from '../../_services/alert.service';
import { UserService } from '../../_services/user.service';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-user-profile-picture',
  templateUrl: './user-profile-picture.component.html',
  styleUrls: ['./user-profile-picture.component.css'],
  standalone: false
})
export class UserProfilePictureComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('cropCanvas') cropCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('previewCanvas') previewCanvasRef!: ElementRef<HTMLCanvasElement>;

  isBrowser = false;
  currentUser: User;
  selectedFile: File | null = null;
  croppedImage: File | null = null;
  croppedImageBase64: string | null = null;
  submitting = false;
  profileImage = '/assets/images/default.jpg';
  loading = true;

  // Canvas cropper state
  private img: HTMLImageElement | null = null;
  private isDragging = false;
  private isResizing = false;
  private resizeHandle = '';
  private dragStartX = 0;
  private dragStartY = 0;
  private cropX = 0;
  private cropY = 0;
  private cropSize = 200;
  private canvasWidth = 0;
  private canvasHeight = 0;
  private imgDrawX = 0;
  private imgDrawY = 0;
  private imgDrawWidth = 0;
  private imgDrawHeight = 0;

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private alertService: AlertService,
    public userService: UserService,
    private translateService: TranslateService,
    private router: Router,
    private cd: ChangeDetectorRef,
    private toastr: ToastrService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.currentUser = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
    } else {
      this.currentUser = {} as User;
    }
  }

  ngOnInit() {
    if (!this.isBrowser) {
      this.loading = false;
      return;
    }

    const sub = this.userService.load().subscribe(
      (user: User) => {
        this.currentUser = user;
        this.profileImage = this.getProfileImageUrl(user);
        this.loading = false;
        this.cd.detectChanges();
      },
      (error) => {
        console.error('[PFP] load error:', error);
        this.loading = false;
        this.cd.detectChanges();
      }
    );
    this.subscriptions.push(sub);
  }

  ngAfterViewInit() {}

  getProfileImageUrl(user: User): string {
    if (user?.picture?.trim()) {
      return `${environment.image_cdn_url}/${user.picture}`;
    }
    return '/assets/images/default.jpg';
  }

  onImageError(): void {
    this.profileImage = '/assets/images/default.jpg';
  }

  changePicture() {
    if (!this.isBrowser) return;
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
      fileInput.click();
    }
  }

  onSelectedPicture(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    const maxSizeBytes = 5 * 1024 * 1024;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      const sub = this.translateService
        .get('user_profile.invalid_file_type')
        .subscribe(msg => {
          const message = msg || 'Invalid file type. Only PNG, JPEG and JPG files are allowed.';
          this.toastr.error(message);
          this.alertService.error(message);
        });
      this.subscriptions.push(sub);
      input.value = '';
      return;
    }

    // Validate file size
    if (file.size > maxSizeBytes) {
      const sub = this.translateService
        .get('user_profile.image_too_large')
        .subscribe(msg => {
          const message = msg || 'Image is too large. Maximum size: 5MB';
          this.toastr.error(message);
          this.alertService.error(message);
        });
      this.subscriptions.push(sub);
      input.value = '';
      return;
    }

    this.selectedFile = file;
    this.croppedImage = null;
    this.croppedImageBase64 = null;
    this.cd.detectChanges();

    const reader = new FileReader();
    reader.onload = (e) => {
      const imgEl = new Image();
      imgEl.onload = () => {
        this.img = imgEl;
        this.cd.detectChanges();
        setTimeout(() => this.initCanvas(), 50);
      };
      imgEl.src = e.target!.result as string;
    };
    reader.readAsDataURL(file);
  }

  private initCanvas() {
    const canvas = this.cropCanvasRef?.nativeElement;
    if (!canvas || !this.img) return;

    const maxW = canvas.parentElement!.clientWidth || 400;
    const maxH = 350;

    // Scale image to fit canvas
    const scale = Math.min(maxW / this.img.width, maxH / this.img.height);
    this.imgDrawWidth = this.img.width * scale;
    this.imgDrawHeight = this.img.height * scale;
    this.canvasWidth = Math.max(this.imgDrawWidth, maxW);
    this.canvasHeight = this.imgDrawHeight;

    canvas.width = this.canvasWidth;
    canvas.height = this.canvasHeight;

    // Center image on canvas
    this.imgDrawX = (this.canvasWidth - this.imgDrawWidth) / 2;
    this.imgDrawY = (this.canvasHeight - this.imgDrawHeight) / 2;

    // Initial crop box: centered square
    this.cropSize = Math.min(this.imgDrawWidth, this.imgDrawHeight) * 0.8;
    this.cropX = this.imgDrawX + (this.imgDrawWidth - this.cropSize) / 2;
    this.cropY = this.imgDrawY + (this.imgDrawHeight - this.cropSize) / 2;

    this.drawCanvas();
    this.updatePreview();
  }

  private drawCanvas() {
    const canvas = this.cropCanvasRef?.nativeElement;
    if (!canvas || !this.img) return;
    const ctx = canvas.getContext('2d')!;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image
    ctx.drawImage(this.img, this.imgDrawX, this.imgDrawY, this.imgDrawWidth, this.imgDrawHeight);

    // Dark overlay
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Cut out crop area (show image clearly inside crop box)
    ctx.save();
    ctx.beginPath();
    ctx.arc(
      this.cropX + this.cropSize / 2,
      this.cropY + this.cropSize / 2,
      this.cropSize / 2,
      0, Math.PI * 2
    );
    ctx.clip();
    ctx.drawImage(this.img, this.imgDrawX, this.imgDrawY, this.imgDrawWidth, this.imgDrawHeight);
    ctx.restore();

    // Circle border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(
      this.cropX + this.cropSize / 2,
      this.cropY + this.cropSize / 2,
      this.cropSize / 2,
      0, Math.PI * 2
    );
    ctx.stroke();

    // Resize handle (bottom-right)
    const hx = this.cropX + this.cropSize;
    const hy = this.cropY + this.cropSize;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(hx - this.cropSize * 0.15, hy - this.cropSize * 0.15, 8, 0, Math.PI * 2);
    ctx.fill();
  }

  private updatePreview() {
    const previewCanvas = this.previewCanvasRef?.nativeElement;
    if (!previewCanvas || !this.img) return;

    const size = 150;
    previewCanvas.width = size;
    previewCanvas.height = size;
    const ctx = previewCanvas.getContext('2d')!;

    // Compute what portion of the original image corresponds to the crop box
    const scaleX = this.img.width / this.imgDrawWidth;
    const scaleY = this.img.height / this.imgDrawHeight;
    const srcX = (this.cropX - this.imgDrawX) * scaleX;
    const srcY = (this.cropY - this.imgDrawY) * scaleY;
    const srcSize = this.cropSize * scaleX;

    // Clip to circle
    ctx.clearRect(0, 0, size, size);
    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(this.img, srcX, srcY, srcSize, srcSize, 0, 0, size, size);
    ctx.restore();

    this.croppedImageBase64 = previewCanvas.toDataURL('image/jpeg', 0.9);

    // Build croppedImage File for upload
    previewCanvas.toBlob((blob) => {
      if (blob) {
        this.croppedImage = new File([blob], 'profile-picture.jpeg', { type: 'image/jpeg' });
      }
    }, 'image/jpeg', 0.9);

    this.cd.detectChanges();
  }

  // Mouse events
  onCanvasMouseDown(event: MouseEvent) {
    const pos = this.getCanvasPos(event);
    const cx = this.cropX + this.cropSize / 2;
    const cy = this.cropY + this.cropSize / 2;
    const handleX = this.cropX + this.cropSize - this.cropSize * 0.15;
    const handleY = this.cropY + this.cropSize - this.cropSize * 0.15;

    // Check resize handle
    if (Math.hypot(pos.x - handleX, pos.y - handleY) < 12) {
      this.isResizing = true;
      this.dragStartX = pos.x;
      this.dragStartY = pos.y;
      return;
    }

    // Check if inside circle
    if (Math.hypot(pos.x - cx, pos.y - cy) < this.cropSize / 2) {
      this.isDragging = true;
      this.dragStartX = pos.x - this.cropX;
      this.dragStartY = pos.y - this.cropY;
    }
  }

  onCanvasMouseMove(event: MouseEvent) {
    if (!this.isDragging && !this.isResizing) return;
    const pos = this.getCanvasPos(event);

    if (this.isResizing) {
      const delta = (pos.x - this.dragStartX + pos.y - this.dragStartY) / 2;
      const newSize = Math.max(50, Math.min(
        this.cropSize + delta,
        this.imgDrawWidth,
        this.imgDrawHeight
      ));
      this.cropSize = newSize;
      // Keep crop box within image bounds
      this.cropX = Math.max(this.imgDrawX, Math.min(this.cropX, this.imgDrawX + this.imgDrawWidth - this.cropSize));
      this.cropY = Math.max(this.imgDrawY, Math.min(this.cropY, this.imgDrawY + this.imgDrawHeight - this.cropSize));
      this.dragStartX = pos.x;
      this.dragStartY = pos.y;
    }

    if (this.isDragging) {
      this.cropX = Math.max(this.imgDrawX, Math.min(pos.x - this.dragStartX, this.imgDrawX + this.imgDrawWidth - this.cropSize));
      this.cropY = Math.max(this.imgDrawY, Math.min(pos.y - this.dragStartY, this.imgDrawY + this.imgDrawHeight - this.cropSize));
    }

    this.drawCanvas();
    this.updatePreview();
  }

  onCanvasMouseUp() {
    this.isDragging = false;
    this.isResizing = false;
  }

  // Touch events
  onCanvasTouchStart(event: TouchEvent) {
    event.preventDefault();
    const touch = event.touches[0];
    this.onCanvasMouseDown({ clientX: touch.clientX, clientY: touch.clientY } as MouseEvent);
  }

  onCanvasTouchMove(event: TouchEvent) {
    event.preventDefault();
    const touch = event.touches[0];
    this.onCanvasMouseMove({ clientX: touch.clientX, clientY: touch.clientY } as MouseEvent);
  }

  onCanvasTouchEnd() {
    this.onCanvasMouseUp();
  }

  private getCanvasPos(event: MouseEvent): { x: number; y: number } {
    const canvas = this.cropCanvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height)
    };
  }

  onCancelImageChangedEvent() {
    this.selectedFile = null;
    this.croppedImage = null;
    this.croppedImageBase64 = null;
    this.img = null;
    this.cd.detectChanges();
    if (!this.isBrowser) return;
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }

  uploadPicture() {
    if (!this.croppedImage) return;
    this.submitting = true;

    const uploadSub = this.userService
      .uploadPicture(this.currentUser, this.croppedImage)
      .subscribe(
        () => {
          const loadSub = this.userService.load().subscribe(
            (user: User) => {
              this.userService.setCurrentUser(user);
              this.router.navigate(['/user/dashboard']);
            },
            (error) => {
              console.error('[PFP] reload error:', error);
              this.submitting = false;
              this.cd.detectChanges();
            }
          );
          this.subscriptions.push(loadSub);
        },
        (error) => {
          console.error('[PFP] upload error:', error);
          this.submitting = false;
          this.cd.detectChanges();
          const sub = this.translateService
            .get('user_profile.upload_error')
            .subscribe(msg => {
              const message = msg || 'Upload failed. Please try again.';
              this.toastr.error(message);
              this.alertService.error(message);
            });
          this.subscriptions.push(sub);
        }
      );
    this.subscriptions.push(uploadSub);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}